import React from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { PiPlus, PiTrash } from "react-icons/pi";
import { EducaeetStats } from "../../educaeet";
import { HokStats } from "../../hok";
import { CollegeStats } from "../../college";

export default function StatsSection({ content, onContentChange, isSelected }: SectionContentProps) {
    const primaryColor = content.colorPalette?.primary || "#3B82F6";
    const fontFamily = content.fontFamily || "'Inter', sans-serif";
    const variant = content.variant || "v1";

    if (variant === "college") {
        return <CollegeStats {...{ content, onContentChange, isSelected }} />;
    }

    if (variant === "hok") {
        return <HokStats content={content} onContentChange={onContentChange} isSelected={isSelected} />;
    }

    if (variant === 'educaeet') {
        return <EducaeetStats {...{ content, onContentChange, isSelected }} />;
    }

    const stats = content.stats || [
        { value: "40K+", label: "Users" },
        { value: "500+", label: "Companies" },
        { value: "99%", label: "Satisfaction" },
    ];

    const addStat = () => {
        const newStats = [...stats, { value: "0+", label: "New Stat" }];
        onContentChange?.({ ...content, stats: newStats });
    };

    const removeStat = (idx: number) => {
        const newStats = stats.filter((_: any, i: number) => i !== idx);
        onContentChange?.({ ...content, stats: newStats });
    };

    const updateStat = (idx: number, field: string, value: string) => {
        const newStats = [...stats];
        newStats[idx] = { ...newStats[idx], [field]: value };
        onContentChange?.({ ...content, stats: newStats });
    };

    return (
        <div
            className={`bg-white px-8 py-16 ${isSelected ? "ring-2 ring-blue-500" : ""}`}
            style={{ fontFamily }}
        >
            <div className="mx-auto max-w-6xl">
                {content.title && (
                    <h2 className="mb-12 text-center text-3xl font-bold text-gray-900">
                        <EditableText
                            value={content.title}
                            onChange={(val) => onContentChange?.({ ...content, title: val })}
                            as="span"
                            placeholder="Stats Title"
                        />
                    </h2>
                )}

                <div className={`grid gap-8 ${variant === 'v2' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-2 lg:grid-cols-3'}`}>
                    {stats.map((stat: any, idx: number) => (
                        <div
                            key={idx}
                            className={`group relative flex flex-col items-center justify-center p-6 text-center transition-all ${variant === 'v2'
                                    ? 'bg-white rounded-2xl shadow-lg border border-gray-100 hover:-translate-y-1'
                                    : 'hover:bg-gray-50 rounded-xl'
                                }`}
                        >
                            {isSelected && (
                                <button
                                    onClick={(e) => { e.stopPropagation(); removeStat(idx); }}
                                    className="absolute top-2 right-2 hidden group-hover:flex h-6 w-6 items-center justify-center rounded-full bg-red-100 text-red-500 hover:bg-red-200"
                                    title="Remove Stat"
                                >
                                    <PiTrash className="h-3 w-3" />
                                </button>
                            )}

                            <div
                                className="text-3xl md:text-4xl lg:text-3xl xl:text-4xl font-extrabold mb-2 tracking-tight w-full whitespace-nowrap px-2"
                                style={{ color: primaryColor }}
                            >
                                <EditableText
                                    value={stat.value}
                                    onChange={(val) => updateStat(idx, "value", val)}
                                    as="span"
                                    placeholder="0"
                                />
                            </div>
                            <div className="text-xs sm:text-sm font-semibold text-gray-500 uppercase tracking-wider w-full px-2">
                                <EditableText
                                    value={stat.label}
                                    onChange={(val) => updateStat(idx, "label", val)}
                                    as="span"
                                    placeholder="Label"
                                />
                            </div>
                        </div>
                    ))}

                    {isSelected && (
                        <button
                            onClick={addStat}
                            className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 p-6 text-gray-400 hover:border-blue-400 hover:bg-blue-50 hover:text-blue-500 transition-all min-h-[160px]"
                        >
                            <PiPlus className="h-6 w-6 mb-2" />
                            <span className="text-xs font-medium">Add Stat</span>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
